import React, { useState } from 'react';
import { Clock, CheckCircle, AlertCircle, Trash2, RefreshCw } from 'lucide-react';

export interface Ticket {
  id: string;
  type: 'CLEANING' | 'MAINTENANCE' | 'SUPPLY';
  status: 'OPEN' | 'IN_PROGRESS' | 'COMPLETED';
  priority: 'LOW' | 'MEDIUM' | 'HIGH';
  createdAt: Date;
  updatedAt: Date;
  area?: string;
  reason?: string;
  system?: string;
  issue?: string;
  item?: string;
  level?: number;
  assignedTo?: string;
  notes?: string[];
}

interface TicketManagementProps {
  tickets: Ticket[];
  onUpdateTicket: (ticketId: string, updates: Partial<Ticket>) => void;
  onDeleteTicket: (ticketId: string) => void;
}

const TicketManagement: React.FC<TicketManagementProps> = ({
  tickets,
  onUpdateTicket,
  onDeleteTicket
}) => {
  const [filterStatus, setFilterStatus] = useState<Ticket['status'] | 'ALL'>('ALL');
  const [filterType, setFilterType] = useState<Ticket['type'] | 'ALL'>('ALL');

  const getStatusColor = (status: Ticket['status']) => {
    switch (status) {
      case 'OPEN':
        return 'bg-red-100 text-red-800';
      case 'IN_PROGRESS':
        return 'bg-yellow-100 text-yellow-800';
      case 'COMPLETED':
        return 'bg-green-100 text-green-800';
    }
  };

  const getPriorityColor = (priority: Ticket['priority']) => {
    switch (priority) {
      case 'LOW':
        return 'text-blue-600';
      case 'MEDIUM':
        return 'text-yellow-600';
      case 'HIGH':
        return 'text-red-600';
    }
  };

  const filteredTickets = tickets.filter(ticket => {
    if (filterStatus !== 'ALL' && ticket.status !== filterStatus) return false;
    if (filterType !== 'ALL' && ticket.type !== filterType) return false;
    return true;
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Facility Tickets</h3>
        <div className="flex space-x-4">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as Ticket['type'] | 'ALL')}
            className="border rounded px-2 py-1"
          >
            <option value="ALL">All Types</option>
            <option value="CLEANING">Cleaning</option>
            <option value="MAINTENANCE">Maintenance</option>
            <option value="SUPPLY">Supply</option>
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as Ticket['status'] | 'ALL')}
            className="border rounded px-2 py-1"
          >
            <option value="ALL">All Status</option>
            <option value="OPEN">Open</option>
            <option value="IN_PROGRESS">In Progress</option>
            <option value="COMPLETED">Completed</option>
          </select>
        </div>
      </div>

      <div className="grid gap-4">
        {filteredTickets.map((ticket) => (
          <div key={ticket.id} className="border rounded-lg p-4 bg-white shadow-sm">
            <div className="flex justify-between items-start mb-2">
              <div>
                <span className={`px-2 py-1 rounded-full text-sm ${getStatusColor(ticket.status)}`}>
                  {ticket.status.replace('_', ' ')}
                </span>
                <span className={`ml-2 font-medium ${getPriorityColor(ticket.priority)}`}>
                  {ticket.priority} Priority
                </span>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    const newStatus = ticket.status === 'OPEN' ? 'IN_PROGRESS' : 
                                   ticket.status === 'IN_PROGRESS' ? 'COMPLETED' : 'OPEN';
                    onUpdateTicket(ticket.id, { status: newStatus });
                  }}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  {ticket.status === 'COMPLETED' ? (
                    <RefreshCw className="w-5 h-5 text-gray-500" />
                  ) : (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  )}
                </button>
                <button
                  onClick={() => onDeleteTicket(ticket.id)}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <Trash2 className="w-5 h-5 text-red-500" />
                </button>
              </div>
            </div>

            <div className="space-y-2">
              {ticket.type === 'CLEANING' && (
                <>
                  <p className="font-medium">Area: {ticket.area}</p>
                  <p className="text-gray-600">Reason: {ticket.reason}</p>
                </>
              )}
              {ticket.type === 'MAINTENANCE' && (
                <>
                  <p className="font-medium">System: {ticket.system}</p>
                  <p className="text-gray-600">Issue: {ticket.issue}</p>
                </>
              )}
              {ticket.type === 'SUPPLY' && (
                <>
                  <p className="font-medium">Item: {ticket.item}</p>
                  <p className="text-gray-600">Current Level: {ticket.level}</p>
                </>
              )}
              {ticket.assignedTo && (
                <p className="text-sm text-gray-500">Assigned to: {ticket.assignedTo}</p>
              )}
              <div className="flex items-center text-sm text-gray-500">
                <Clock className="w-4 h-4 mr-1" />
                Created: {ticket.createdAt.toLocaleString()}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TicketManagement; 